import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from './api';
import HamburgerMenu from './HamburgerMenu';

// Basic styling for the page and modals
const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        background: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#212529',
        border: isDark ? '#444' : '#dee2e6',
        link: isDark ? '#6cb2f7' : '#007bff',
        buttonBg: isDark ? '#6cb2f7' : '#007bff',
        buttonText: isDark ? '#121212' : '#fff',
        menuItemColor: isDark ? '#e0e0e0' : '#333',
        modalBg: isDark ? '#2a2a2a' : '#fff',
    };

    return {
        container: { width: '90%', maxWidth: '1000px', margin: '20px auto', fontFamily: 'sans-serif', backgroundColor: colors.background, color: colors.text },
        header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
        pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
        pageTitle: { fontSize: '24px', margin: 0 },
        menuItem: {
            padding: '10px 15px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            display: 'block',
            textDecoration: 'none',
            color: colors.menuItemColor,
        },
        menuItemButton: {
            background: 'none',
            border: 'none',
            padding: '10px 15px',
            width: '100%',
            textAlign: 'left',
            cursor: 'pointer',
            fontSize: '16px',
            fontFamily: 'sans-serif',
            color: colors.menuItemColor,
        },
        table: { width: '100%', borderCollapse: 'collapse', marginTop: '20px' },
        th: { borderBottom: `2px solid ${colors.border}`, padding: '12px', textAlign: 'left' },
        td: { borderBottom: `1px solid ${colors.border}`, padding: '12px' },
        button: { padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: '4px', cursor: 'pointer', marginRight: '5px', backgroundColor: colors.buttonBg, color: colors.buttonText },
        modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center' },
        modalContent: { backgroundColor: colors.modalBg, padding: '20px', borderRadius: '8px', width: '400px', color: colors.text },
        formGroup: { marginBottom: '15px' },
        label: { display: 'block', marginBottom: '5px' },
        input: { width: '100%', padding: '8px', boxSizing: 'border-box', backgroundColor: colors.background, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: '4px' },
    };
};


function formatLastLogin(value) {
    if (!value) return 'Never';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

function AdminUserManagementPage({ theme }) {
    const styles = getStyles(theme);
    const [users, setUsers] = useState([]);
    const [modal, setModal] = useState({ isOpen: false, type: null, user: null });
    const [uploadModal, setUploadModal] = useState({ isOpen: false, file: null, uploading: false });
    const [uploadResult, setUploadResult] = useState(null);

    // Fetch initial data
    useEffect(() => {
        fetchUsers();
        // We can fetch groups from the user list, but a dedicated endpoint is cleaner if available
    }, []);

    const fetchUsers = () => {
        api.get('/users/')
            .then(res => {
                // Ensure we have an array
                const usersData = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setUsers(usersData);
            })
            .catch(err => {
                console.error("Error fetching users:", err);
                setUsers([]); // Set to empty array on error
            });
    };

    const handleSave = (userData) => {
        const { id, ...data } = userData;
        
        // Convert groups string to array if it exists
        if (data.groups && typeof data.groups === 'string') {
            data.groups = data.groups.split(',').map(group => group.trim()).filter(group => group.length > 0);
        }
        
        const apiCall = id 
            ? api.put(`/users/${id}/`, data) // Update existing user
            : api.post('/users/', data);     // Create new user

        apiCall.then(() => {
            fetchUsers();
            setModal({ isOpen: false, type: null, user: null });
        }).catch(err => {
            console.error("Error saving user:", err.response.data);
            alert("Error: " + JSON.stringify(err.response.data));
        });
    };

    const handleResetPassword = (userId, newPassword) => {
        api.post(`/users/${userId}/set_password/`, { new_password: newPassword })
            .then(() => {
                alert("Password reset successfully.");
                setModal({ isOpen: false, type: null, user: null });
            }).catch(err => {
                console.error("Error resetting password:", err.response.data);
                alert("Error: " + JSON.stringify(err.response.data));
            });
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            setUploadModal({ isOpen: true, file: file, uploading: false });
        }
    };

    const handleBulkUpload = () => {
        if (!uploadModal.file) return;

        setUploadModal(prev => ({ ...prev, uploading: true }));
        setUploadResult(null);

        const formData = new FormData();
        formData.append('file', uploadModal.file);

        api.post('/upload-users/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
        .then(response => {
            setUploadResult(response.data);
            fetchUsers(); // Refresh the user list
            setUploadModal({ isOpen: false, file: null, uploading: false });
        })
        .catch(err => {
            console.error("Error uploading users:", err.response?.data || err.message);
            setUploadResult({ 
                error: err.response?.data?.error || "An error occurred during upload" 
            });
            setUploadModal(prev => ({ ...prev, uploading: false }));
        });
    };

    const renderModal = () => {
        if (!modal.isOpen) return null;
        const isNewUser = modal.type === 'new';
        const isEdit = modal.type === 'edit';
        const isPassword = modal.type === 'password';

        // Simplified form components for brevity
        const UserForm = () => {
            const [formData, setFormData] = useState(isNewUser ? { username: '', password: '' } : { 
                ...modal.user, 
                groups: Array.isArray(modal.user.groups) ? modal.user.groups.join(', ') : modal.user.groups || ''
            });
            const handleInputChange = (e) => setFormData({...formData, [e.target.name]: e.target.value});
            
            return (
                 <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <h2>{isNewUser ? 'Create New User' : 'Edit User'}</h2>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Username</label>
                            <input name="username" value={formData.username} onChange={handleInputChange} style={styles.input} />
                        </div>
                         {isNewUser && <div style={styles.formGroup}>
                            <label style={styles.label}>Password</label>
                            <input type="password" name="password" value={formData.password} onChange={handleInputChange} style={styles.input} />
                        </div>}
                        {/* A more complex implementation would fetch all groups and show checkboxes */}
                        {isEdit && <div style={styles.formGroup}><label style={styles.label}>Groups (comma-separated):</label><input name="groups" value={formData.groups} onChange={handleInputChange} style={styles.input} /></div>}
                        <button style={styles.button} onClick={() => handleSave(formData)}>Save</button>
                        <button style={styles.button} onClick={() => setModal({isOpen: false})}>Cancel</button>
                    </div>
                </div>
            );
        };
        
        const PasswordForm = () => {
            const [newPassword, setNewPassword] = useState('');
            return (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <h2>Reset Password for {modal.user.username}</h2>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>New Password</label>
                            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} style={styles.input} />
                        </div>
                        <button style={styles.button} onClick={() => handleResetPassword(modal.user.id, newPassword)}>Set Password</button>
                        <button style={styles.button} onClick={() => setModal({isOpen: false})}>Cancel</button>
                    </div>
                </div>
            );
        };

        if(isNewUser || isEdit) return <UserForm />;
        if(isPassword) return <PasswordForm />;
        return null;
    };

    const renderUploadModal = () => {
        if (!uploadModal.isOpen) return null;

        return (
            <div style={styles.modalOverlay}>
                <div style={styles.modalContent}>
                    <h2>Bulk Import Users</h2>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Selected File:</label>
                        <div style={{ padding: '8px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                            {uploadModal.file?.name}
                        </div>
                    </div>
                    
                    {uploadResult && (
                        <div style={styles.formGroup}>
                            {uploadResult.error ? (
                                <div style={{ color: '#dc3545', padding: '10px', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px' }}>
                                    <strong>Error:</strong> {uploadResult.error}
                                </div>
                            ) : (
                                <div style={{ color: '#155724', padding: '10px', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '4px' }}>
                                    <strong>Success!</strong><br/>
                                    {uploadResult.message}<br/>
                                    {uploadResult.errors && uploadResult.errors.length > 0 && (
                                        <div style={{ marginTop: '10px' }}>
                                            <strong>Errors:</strong>
                                            <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                                                {uploadResult.errors.slice(0, 5).map((error, index) => (
                                                    <li key={index} style={{ fontSize: '12px' }}>{error}</li>
                                                ))}
                                            </ul>
                                            {uploadResult.errors.length > 5 && (
                                                <div style={{ fontSize: '12px' }}>... and {uploadResult.errors.length - 5} more errors</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                        <button 
                            style={styles.button} 
                            onClick={handleBulkUpload}
                            disabled={uploadModal.uploading}
                        >
                            {uploadModal.uploading ? 'Uploading...' : 'Upload Users'}
                        </button>
                        <button 
                            style={styles.button} 
                            onClick={() => {
                                setUploadModal({ isOpen: false, file: null, uploading: false });
                                setUploadResult(null);
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        );
    };


    return (
        <div style={styles.container}>
            <div style={styles.pageHeader}>
                <HamburgerMenu theme={theme}/>
                <h1 style={styles.pageTitle}>User Management</h1>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <Link to="/account-requests" style={{ ...styles.button, textDecoration: 'none', display: 'inline-block' }}>
                        Account Requests
                    </Link>
                    <input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                        id="user-upload-input"
                    />
                    <label htmlFor="user-upload-input" style={{ ...styles.button, cursor: 'pointer', display: 'inline-block' }}>
                        Import Users (CSV/Excel)
                    </label>
                    <button style={styles.button} onClick={() => setModal({ isOpen: true, type: 'new', user: null })}>Create New User</button>
                </div>
            </div>

            <table style={styles.table}>
                <thead>
                    <tr>
                        <th style={styles.th}>Username</th>
                        <th style={styles.th}>Groups</th>
                        <th style={styles.th}>Staff Status</th>
                        <th style={styles.th}>Last Login</th>
                        <th style={styles.th}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {Array.isArray(users) && users.map(user => (
                        <tr key={user.id}>
                            <td style={styles.td}>{user.username}</td>
                            <td style={styles.td}>{user.groups.join(', ')}</td>
                            <td style={styles.td}>{user.is_staff ? 'Yes' : 'No'}</td>
                            <td style={styles.td}>{formatLastLogin(user.last_login)}</td>
                            <td style={styles.td}>
                                <button style={styles.button} onClick={() => setModal({ isOpen: true, type: 'edit', user })}>Edit</button>
                                <button style={styles.button} onClick={() => setModal({ isOpen: true, type: 'password', user })}>Reset Password</button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {renderModal()}
            {renderUploadModal()}
        </div>
    );
}

export default AdminUserManagementPage;
